import React from 'react';
import { ScrollLink } from './ScrollLink';
import useSEO from '../hooks/useSEO';
import '../styles/InformationalPage.css';

const pages = {
  ajuda: {
    title: 'Central de Ajuda',
    seoTitle: 'Central de Ajuda | ABRALIBER',
    description: 'Encontre respostas sobre cursos, acesso à plataforma, certificados digitais e atendimento da ABRALIBER.',
    intro: 'Encontre respostas rápidas sobre acesso, estudos, certificados e atendimento.',
    sections: [
      {
        heading: 'Acesso aos cursos',
        body: 'Os cursos da ABRALIBER são online e gratuitos. Para começar, escolha um curso, faça seu cadastro e acesse o conteúdo na área do aluno.',
      },
      {
        heading: 'Certificados',
        body: 'Depois de concluir os requisitos do curso, você pode solicitar o certificado digital de conclusão. A emissão é opcional e disponibilizada mediante pagamento.',
      },
      {
        heading: 'Atendimento',
        body: 'Se precisar de ajuda, fale com a equipe pelo e-mail contato@abraliber.com.br ou pelo WhatsApp (61) 99623-0013 em horário comercial.',
      },
    ],
  },
  faq: {
    title: 'Perguntas Frequentes',
    seoTitle: 'Perguntas Frequentes | ABRALIBER',
    description: 'Dúvidas frequentes sobre cursos online gratuitos, certificados digitais e funcionamento da ABRALIBER.',
    intro: 'As principais dúvidas sobre a experiência de estudo na ABRALIBER.',
    sections: [
      {
        heading: 'Preciso pagar para estudar?',
        body: 'Não. O acesso ao conteúdo dos cursos é gratuito.',
      },
      {
        heading: 'O certificado é gratuito?',
        body: 'Não. O certificado digital de conclusão é opcional e disponibilizado mediante pagamento após a conclusão do curso.',
      },
      {
        heading: 'Os cursos são reconhecidos pelo MEC?',
        body: 'Cursos livres não dependem de reconhecimento do MEC. O certificado serve como comprovação de conclusão de curso livre, conforme as regras de quem o receber.',
      },
      {
        heading: 'Posso estudar pelo celular?',
        body: 'Sim. A plataforma funciona em celular, tablet e computador.',
      },
    ],
  },
  privacidade: {
    title: 'Política de Privacidade',
    seoTitle: 'Política de Privacidade | ABRALIBER',
    description: 'Política de privacidade da ABRALIBER para uso da plataforma de cursos online.',
    intro: 'Esta política resume como a ABRALIBER trata dados pessoais na plataforma.',
    sections: [
      {
        heading: 'Dados coletados',
        body: 'Podemos coletar dados de cadastro, contato, acesso aos cursos, progresso, avaliações, pagamentos e emissão de certificados.',
      },
      {
        heading: 'Uso dos dados',
        body: 'Usamos os dados para liberar acesso, registrar matrículas, acompanhar progresso, prestar suporte, processar pagamentos, emitir certificados quando solicitados e entender buscas, páginas e recursos utilizados para melhorar a experiência na plataforma.',
      },
      {
        heading: 'Dados de navegação',
        body: 'Registramos eventos de navegação, como páginas visitadas, termos buscados, cliques em cursos ou comandos da interface, endereço IP da conexão e informações gerais do dispositivo, navegador e sistema operacional. Quando há login, esses eventos podem ser associados à conta; sem login, são vinculados a uma sessão aleatória. Esses dados são usados para segurança, análise de acesso, estimativas de localidade e melhoria da plataforma. Não registramos o conteúdo digitado em formulários nesse histórico de análise.',
      },
      {
        heading: 'Segurança e direitos',
        body: 'Adotamos medidas de proteção compatíveis com a operação da plataforma. Para solicitar informações, correção ou exclusão de dados, entre em contato pelo e-mail contato@abraliber.com.br.',
      },
    ],
  },
  termos: {
    title: 'Termos de Uso',
    seoTitle: 'Termos de Uso | ABRALIBER',
    description: 'Termos de uso da plataforma de cursos online da ABRALIBER.',
    intro: 'Ao usar a plataforma, você concorda com as condições abaixo.',
    sections: [
      {
        heading: 'Uso da plataforma',
        body: 'O acesso aos cursos é pessoal e deve ser usado de forma lícita, respeitosa e compatível com a finalidade educacional da ABRALIBER.',
      },
      {
        heading: 'Cursos e certificados',
        body: 'Os cursos são livres e gratuitos para estudo. O certificado digital de conclusão é opcional, condicionado ao cumprimento dos requisitos do curso e ao pagamento da emissão quando aplicável.',
      },
      {
        heading: 'Conteúdo e disponibilidade',
        body: 'A ABRALIBER pode atualizar conteúdos, recursos e regras da plataforma para melhorar a experiência dos alunos e manter a operação do serviço.',
      },
    ],
  },
  acessibilidade: {
    title: 'Acessibilidade',
    seoTitle: 'Acessibilidade | ABRALIBER',
    description: 'Compromissos de acessibilidade da ABRALIBER para a plataforma de cursos online.',
    intro: 'A ABRALIBER trabalha para tornar a experiência de estudo mais acessível e inclusiva.',
    sections: [
      {
        heading: 'Compatibilidade',
        body: 'A plataforma é desenvolvida para funcionar em diferentes tamanhos de tela e dispositivos, com navegação simples e conteúdo organizado.',
      },
      {
        heading: 'Melhorias contínuas',
        body: 'Acessibilidade é um processo contínuo. Revisamos textos, contraste, navegação e estrutura das páginas conforme identificamos oportunidades de melhoria.',
      },
      {
        heading: 'Solicitações',
        body: 'Caso encontre barreiras de acesso, envie uma mensagem para contato@abraliber.com.br com a página, dispositivo e dificuldade encontrada.',
      },
    ],
  },
};

function InformationalPage({ pageKey }) {
  const page = pages[pageKey] || pages.ajuda;

  useSEO({
    title: page.seoTitle,
    description: page.description,
    url: pageKey === 'ajuda' ? '/ajuda' : `/${pageKey === 'faq' ? 'perguntas-frequentes' : pageKey === 'privacidade' ? 'politica-de-privacidade' : pageKey === 'termos' ? 'termos-de-uso' : 'acessibilidade'}`,
  });

  return (
    <main className="informational-page">
      <section className="informational-hero">
        <div className="informational-container">
          <h1>{page.title}</h1>
          <p>{page.intro}</p>
        </div>
      </section>

      <section className="informational-content">
        <div className="informational-container">
          {page.sections.map((section) => (
            <article className="informational-section" key={section.heading}>
              <h2>{section.heading}</h2>
              <p>{section.body}</p>
            </article>
          ))}

          <div className="informational-actions">
            <ScrollLink to="/contato" className="informational-button">
              Falar com a ABRALIBER
            </ScrollLink>
          </div>
        </div>
      </section>
    </main>
  );
}

export default InformationalPage;
